import {
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { getActivityRowId } from "../../utils/activityUtils";
import { RECENT_ACTIVITY_PAGE_SIZE } from "../../utils/activityConstants";

interface RecentActivityProps {
  recentActivity: any[];
}

export const RecentActivity: React.FC<RecentActivityProps> = ({
  recentActivity,
}) => {
  const { t } = useTranslation();

  const recentActivityColumns: GridColDef[] = [
    {
      field: "user",
      headerName: t("screens.superAdmin.recentActivity.columns.user"),
      width: 300,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {params.row.user_name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.2 }}
          >
            {params.row.user_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "fbo_name",
      headerName: t("screens.superAdmin.recentActivity.columns.fbo"),
      width: 250,
      editable: false,
    },
    {
      field: "timestamp",
      headerName: t("screens.superAdmin.recentActivity.columns.timestamp"),
      width: 250,
      editable: false,
      valueGetter: (_, row) => new Date(row.timestamp).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    },
  ];

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{ width: "100%", p: 1 }}
    >
      {recentActivity.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">{t("screens.superAdmin.recentActivity.noActivity")}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 600,
            width: "100%",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor: "background.paper",
            "& .MuiDataGrid-root": {
              border: "none",
            },
          }}
        >
          <DataGrid
            rows={recentActivity.map((activity, idx) => ({
              ...activity,
              id: getActivityRowId(activity, idx),
            }))}
            columns={recentActivityColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: RECENT_ACTIVITY_PAGE_SIZE, page: 0 },
              },
            }}
            pageSizeOptions={[RECENT_ACTIVITY_PAGE_SIZE]}
            disableRowSelectionOnClick
            disableColumnMenu
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-cell": {
                alignItems: "center",
                display: "flex",
                py: 1,
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "action.selected",
                fontSize: "0.875rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "action.hover",
              },
            }}
          />
        </Box>
      )}
    </Stack>
  );
};